import type { RadioScoreData } from '@/components/feature/RadioScore';

export interface CommunityStats {
  followers: number;
  posts: number;
  comments: number;
  events: number;
  interactions: number;
}

export type SARadioMasterStatus =
  | 'solicitud_recibida'
  | 'en_revision'
  | 'aprobada'
  | 'pendiente_contrato'
  | 'pendiente_firma'
  | 'pendiente_pago'
  | 'activa'
  | 'suspendida'
  | 'vencida'
  | 'cancelada'
  | 'archivada';

export type SARadioPlan = 'free' | 'pro' | 'premium';
export type SARadioStreamStatus = 'activo' | 'caido' | 'inactivo';
export type SARadioStreamStatusV2 = 'online' | 'inestable' | 'offline' | 'error' | 'sin_respuesta';

export interface StatusChangeEntry {
  id: number;
  fromStatus: SARadioMasterStatus | null;
  toStatus: SARadioMasterStatus;
  changedBy: string;
  changedAt: string;
  reason: string;
}

export interface SARadio {
  id: number;
  name: string;
  frequency: string;
  city: string;
  country: string;
  plan: SARadioPlan;
  status: SARadioMasterStatus;
  streamStatus: SARadioStreamStatus;
  streamStatusV2: SARadioStreamStatusV2;
  daysOffline: number;
  warningLevel: 'ninguna' | 'advertencia' | 'oculta' | 'suspendida' | 'en_riesgo' | 'critica';
  warningMessage: string | null;
  recommendedAction: string;
  isHiddenFromDirectory: boolean;
  isAtRisk: boolean;
  storageUsedGB: number;
  storageTotalGB: number;
  registeredAt: string;
  listeners: number;
  category: string;
  slogan: string;
  logoUrl: string;
  ownerName: string;
  ownerEmail: string;
  streamUrl: string;
  suspendReason?: string;
  rejectedReason?: string;
  redirectCode: string;
  customDomain: string | null;
  statusHistory: StatusChangeEntry[];
  radioScore: RadioScoreData;
  communityStats: CommunityStats;
}

export const saRadiosData: SARadio[] = [
  {
    id: 1,
    name: 'Radio Nueva Actitud',
    frequency: '94.70 RFW',
    city: 'Santa Rosa',
    country: 'Estados Unidos',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 3.2,
    storageTotalGB: 50,
    registeredAt: '2025-03-15',
    listeners: 1284,
    category: 'Pop / Variado',
    slogan: 'La nueva frecuencia de la radio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20circular%20design%2C%20warm%20golden%20amber%20colors%2C%20sound%20wave%20symbol%2C%20clean%20corporate%20style%2C%20abstract%20geometric%20mark%2C%20high%20contrast%20professional%20brand%20identity&width=120&height=120&seq=sa-radio-logo-1&orientation=squarish',
    ownerName: 'Carlos Mendoza',
    ownerEmail: 'carlos@nuevaactitud.com',
    streamUrl: 'https://stream.nuevaactitud.com/live',
    redirectCode: 'nueva-actitud',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-03-15T10:30:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2025-03-16T09:15:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2025-03-18T14:20:00', reason: 'Documentación verificada. Frecuencia 94.70 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-03-18T14:21:00', reason: 'Contrato generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-03-19T08:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-03-20T11:45:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-03-21T16:30:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 85, state: 'excelente', factors: { streamActivo: 90, publicaciones: 80, eventos: 82, respuestasCabina: 88, lives: 75, interaccion: 90 } },
    communityStats: { followers: 2840, posts: 156, comments: 423, events: 12, interactions: 3840 },
  },
  {
    id: 2,
    name: 'Radio Ritmo Latino',
    frequency: '101.30 RFW',
    city: 'San José',
    country: 'Costa Rica',
    plan: 'free',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.4,
    storageTotalGB: 1,
    registeredAt: '2026-06-08',
    listeners: 342,
    category: 'Latina / Salsa',
    slogan: 'El ritmo que te mueve',
    logoUrl: 'https://readdy.ai/api/search-image?query=Latin%20radio%20station%20logo%20design%2C%20vibrant%20warm%20colors%20red%20orange%20coral%2C%20tropical%20music%20theme%2C%20circular%20emblem%2C%20modern%20flat%20design%2C%20clean%20background%20professional%20brand%20mark&width=120&height=120&seq=sa-radio-logo-2&orientation=squarish',
    ownerName: 'María Fernanda Rojas',
    ownerEmail: 'mfrojas@ritmolatino.cr',
    streamUrl: 'https://stream.ritmolatino.cr/live',
    redirectCode: 'ritmo-latino',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-08T07:15:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-06-08T10:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-06-09T09:30:00', reason: 'Documentación verificada. Plan Free no requiere contrato ni pago.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'María Gómez (SA)', changedAt: '2026-06-09T09:31:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 58, state: 'activa', factors: { streamActivo: 70, publicaciones: 45, eventos: 30, respuestasCabina: 50, lives: 40, interaccion: 65 } },
    communityStats: { followers: 890, posts: 45, comments: 128, events: 3, interactions: 1100 },
  },
  {
    id: 3,
    name: 'Radio Éxitos TV',
    frequency: '94.70 RFW',
    city: 'Ciudad de México',
    country: 'México',
    plan: 'premium',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 18.7,
    storageTotalGB: 500,
    registeredAt: '2024-08-22',
    listeners: 4521,
    category: 'Éxitos / Top 40',
    slogan: 'Todos los éxitos, todo el día',
    logoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20station%20logo%2C%20bold%20modern%20design%2C%20metallic%20silver%20and%20crimson%20red%20accents%2C%20broadcast%20tower%20icon%2C%20sleek%20corporate%20identity%2C%20high%20contrast%20minimalist%20style&width=120&height=120&seq=sa-radio-logo-3&orientation=squarish',
    ownerName: 'Alejandro Fuentes',
    ownerEmail: 'afuentes@exitostv.mx',
    streamUrl: 'https://stream.exitostv.mx/live',
    redirectCode: 'exitos-tv',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2024-08-22T12:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2024-08-23T08:30:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2024-08-25T15:00:00', reason: 'Documentación completa. Frecuencia premium aprobada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2024-08-25T15:01:00', reason: 'Contrato Premium generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2024-08-26T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2024-08-27T14:20:00', reason: 'Firma electrónica completada. Pendiente de pago inicial Premium.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2024-08-28T10:15:00', reason: 'Pago inicial Premium confirmado. Radio activada.' },
    ],
    radioScore: { score: 92, state: 'excelente', factors: { streamActivo: 95, publicaciones: 88, eventos: 90, respuestasCabina: 92, lives: 85, interaccion: 94 } },
    communityStats: { followers: 12500, posts: 432, comments: 1890, events: 28, interactions: 22000 },
  },
  {
    id: 4,
    name: 'Radio Fiesta Mix',
    frequency: '88.50 RFW',
    city: 'Buenos Aires',
    country: 'Argentina',
    plan: 'free',
    status: 'activa',
    streamStatus: 'caido',
    streamStatusV2: 'offline',
    daysOffline: 5,
    warningLevel: 'advertencia',
    warningMessage: '5 días sin stream — Advertencia fuerte. Plan Free: se ocultará en 2 días.',
    recommendedAction: 'advertencia',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.7,
    storageTotalGB: 1,
    registeredAt: '2026-02-14',
    listeners: 876,
    category: 'Fiesta / Electrónica',
    slogan: 'La fiesta no para',
    logoUrl: 'https://readdy.ai/api/search-image?query=Energetic%20radio%20station%20logo%2C%20neon%20pink%20and%20electric%20blue%20colors%2C%20party%20music%20theme%2C%20circular%20abstract%20design%2C%20modern%20vibrant%20brand%20identity%20for%20youth%20audience&width=120&height=120&seq=sa-radio-logo-4&orientation=squarish',
    ownerName: 'Lucía Martínez',
    ownerEmail: 'lucia@fiestamix.ar',
    streamUrl: 'https://stream.fiestamix.ar/live',
    redirectCode: 'fiesta-mix',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-02-14T18:30:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-02-15T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-02-16T11:30:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'María Gómez (SA)', changedAt: '2026-02-16T11:31:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 22, state: 'en_riesgo', factors: { streamActivo: 0, publicaciones: 30, eventos: 15, respuestasCabina: 25, lives: 10, interaccion: 35 } },
    communityStats: { followers: 320, posts: 18, comments: 42, events: 1, interactions: 380 },
  },
  {
    id: 5,
    name: 'Radio Pirata FM',
    frequency: '99.90 RFW',
    city: 'Lima',
    country: 'Perú',
    plan: 'free',
    status: 'suspendida',
    streamStatus: 'inactivo',
    streamStatusV2: 'offline',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.2,
    storageTotalGB: 1,
    registeredAt: '2026-01-05',
    listeners: 0,
    category: 'Rock / Alternativo',
    slogan: 'Rock sin censura',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rebel%20radio%20station%20logo%2C%20dark%20skull%20and%20crossbones%20radio%20theme%2C%20edgy%20underground%20design%2C%20black%20and%20crimson%20colors%2C%20grunge%20aesthetic%20brand%20identity&width=120&height=120&seq=sa-radio-logo-5&orientation=squarish',
    ownerName: 'Diego Ramírez',
    ownerEmail: 'diego@piratafm.pe',
    streamUrl: 'https://stream.piratafm.pe/live',
    redirectCode: 'pirata-fm',
    customDomain: null,
    suspendReason: 'Violación de términos: contenido explícito sin advertencia',
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-01-05T14:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2026-01-06T10:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2026-01-07T16:00:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'Admin Global', changedAt: '2026-01-07T16:01:00', reason: 'Activación directa por plan Free.' },
      { id: 5, fromStatus: 'activa', toStatus: 'suspendida', changedBy: 'María Gómez (SA)', changedAt: '2026-04-12T09:30:00', reason: 'Violación de términos: contenido explícito sin advertencia en horario familiar' },
    ],
    radioScore: { score: 5, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 0, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 0 } },
    communityStats: { followers: 45, posts: 0, comments: 0, events: 0, interactions: 0 },
  },
  {
    id: 6,
    name: 'Radio Sonora Digital',
    frequency: '103.70 RFW',
    city: 'Hermosillo',
    country: 'México',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 12.4,
    storageTotalGB: 50,
    registeredAt: '2025-06-10',
    listeners: 2103,
    category: 'Regional Mexicano',
    slogan: 'El sonido de la tierra',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%2C%20warm%20terracotta%20and%20sand%20colors%2C%20acoustic%20guitar%20and%20sombrero%20elements%2C%20circular%20traditional%20modern%20fusion%20design%2C%20professional%20brand%20identity&width=120&height=120&seq=sa-radio-logo-6&orientation=squarish',
    ownerName: 'José Luis Valdez',
    ownerEmail: 'jlvaldez@sonoradigital.mx',
    streamUrl: 'https://stream.sonoradigital.mx/live',
    redirectCode: 'sonora-digital',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-06-10T08:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2025-06-11T09:30:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2025-06-13T12:00:00', reason: 'Documentación verificada. Frecuencia 103.70 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-06-13T12:01:00', reason: 'Contrato Pro generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-06-14T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-06-16T10:45:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-06-17T15:30:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 78, state: 'muy_activa', factors: { streamActivo: 85, publicaciones: 72, eventos: 68, respuestasCabina: 80, lives: 65, interaccion: 82 } },
    communityStats: { followers: 4100, posts: 210, comments: 680, events: 15, interactions: 5600 },
  },
  {
    id: 7,
    name: 'Radio Stereo Visión',
    frequency: '96.10 RFW',
    city: 'Medellín',
    country: 'Colombia',
    plan: 'premium',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 45.2,
    storageTotalGB: 500,
    registeredAt: '2024-11-30',
    listeners: 5678,
    category: 'Urbano / Reguetón',
    slogan: 'Visión de barrio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Urban%20radio%20station%20logo%2C%20graffiti%20street%20art%20style%2C%20vibrant%20purple%20and%20lime%20green%20colors%2C%20city%20skyline%20element%2C%20modern%20hip%20hop%20culture%20brand%20identity&width=120&height=120&seq=sa-radio-logo-7&orientation=squarish',
    ownerName: 'Santiago Ochoa',
    ownerEmail: 'sochoa@stereovision.co',
    streamUrl: 'https://stream.stereovision.co/live',
    redirectCode: 'stereo-vision',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2024-11-30T16:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2024-12-01T08:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2024-12-03T11:00:00', reason: 'Documentación verificada. Frecuencia Premium aprobada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2024-12-03T11:01:00', reason: 'Contrato Premium generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2024-12-04T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2024-12-05T13:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2024-12-06T10:30:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 89, state: 'excelente', factors: { streamActivo: 92, publicaciones: 84, eventos: 88, respuestasCabina: 91, lives: 82, interaccion: 90 } },
    communityStats: { followers: 9800, posts: 380, comments: 1520, events: 22, interactions: 17800 },
  },
  {
    id: 8,
    name: 'Radio Clásica FM',
    frequency: '88.90 RFW',
    city: 'Santiago',
    country: 'Chile',
    plan: 'premium',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 8.1,
    storageTotalGB: 500,
    registeredAt: '2025-09-18',
    listeners: 1456,
    category: 'Clásica / Instrumental',
    slogan: 'La música que trasciende',
    logoUrl: 'https://readdy.ai/api/search-image?query=Elegant%20classical%20radio%20station%20logo%2C%20sophisticated%20gold%20and%20ivory%20palette%2C%20violin%20and%20orchestra%20elements%2C%20refined%20circular%20emblem%2C%20luxury%20brand%20identity%20for%20cultured%20audience&width=120&height=120&seq=sa-radio-logo-8&orientation=squarish',
    ownerName: 'Patricia Vega',
    ownerEmail: 'pvega@clasicafm.cl',
    streamUrl: 'https://stream.clasicafm.cl/live',
    redirectCode: 'clasica-fm',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-09-18T10:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2025-09-19T08:30:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2025-09-21T14:00:00', reason: 'Documentación verificada. Frecuencia Premium aprobada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-09-21T14:01:00', reason: 'Contrato Premium generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-09-22T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-09-24T11:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-09-25T16:45:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 65, state: 'muy_activa', factors: { streamActivo: 78, publicaciones: 58, eventos: 55, respuestasCabina: 62, lives: 50, interaccion: 68 } },
    communityStats: { followers: 2100, posts: 98, comments: 310, events: 8, interactions: 2800 },
  },
  {
    id: 9,
    name: 'Radio Urbana 101',
    frequency: '101.10 RFW',
    city: 'Caracas',
    country: 'Venezuela',
    plan: 'free',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.5,
    storageTotalGB: 1,
    registeredAt: '2026-04-20',
    listeners: 567,
    category: 'Hip Hop / Rap',
    slogan: 'La calle tiene voz',
    logoUrl: 'https://readdy.ai/api/search-image?query=Street%20culture%20radio%20logo%2C%20bold%20graffiti%20typography%2C%20urban%20concrete%20texture%2C%20orange%20and%20black%20color%20scheme%2C%20edgy%20underground%20brand%20identity%20for%20hip%20hop%20station&width=120&height=120&seq=sa-radio-logo-9&orientation=squarish',
    ownerName: 'Andrés Castillo',
    ownerEmail: 'acastillo@urbana101.ve',
    streamUrl: 'https://stream.urbana101.ve/live',
    redirectCode: 'urbana-101',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-04-20T12:30:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-04-21T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-04-22T10:30:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'María Gómez (SA)', changedAt: '2026-04-22T10:31:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 52, state: 'activa', factors: { streamActivo: 65, publicaciones: 40, eventos: 25, respuestasCabina: 48, lives: 35, interaccion: 55 } },
    communityStats: { followers: 720, posts: 35, comments: 95, events: 2, interactions: 890 },
  },
  {
    id: 10,
    name: 'Radio Élite Digital',
    frequency: '107.50 RFW',
    city: 'Madrid',
    country: 'España',
    plan: 'premium',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 67.3,
    storageTotalGB: 500,
    registeredAt: '2024-05-12',
    listeners: 8934,
    category: 'Noticias / Debate',
    slogan: 'Información que importa',
    logoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%2C%20professional%20serious%20design%2C%20navy%20blue%20and%20silver%20metallic%2C%20globe%20and%20microphone%20elements%2C%20authoritative%20corporate%20brand%20identity%20for%20journalism&width=120&height=120&seq=sa-radio-logo-10&orientation=squarish',
    ownerName: 'Rafael Torres',
    ownerEmail: 'rtorres@elitedigital.es',
    streamUrl: 'https://stream.elitedigital.es/live',
    redirectCode: 'elite-digital',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2024-05-12T15:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2024-05-13T08:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2024-05-15T12:00:00', reason: 'Documentación verificada. Frecuencia Premium aprobada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2024-05-15T12:01:00', reason: 'Contrato Premium generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2024-05-16T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2024-05-18T14:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2024-05-19T11:30:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 95, state: 'excelente', factors: { streamActivo: 98, publicaciones: 92, eventos: 94, respuestasCabina: 96, lives: 90, interaccion: 95 } },
    communityStats: { followers: 15600, posts: 520, comments: 2340, events: 35, interactions: 28500 },
  },
  {
    id: 11,
    name: 'La Voz del Llano',
    frequency: '92.30 RFW',
    city: 'Villavicencio',
    country: 'Colombia',
    plan: 'free',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.3,
    storageTotalGB: 1,
    registeredAt: '2026-06-07',
    listeners: 234,
    category: 'Llanera / Folclor',
    slogan: 'El sentir de la tierra',
    logoUrl: 'https://readdy.ai/api/search-image?query=Folk%20radio%20station%20logo%2C%20traditional%20rural%20design%2C%20earth%20tones%20green%20and%20brown%2C%20harp%20and%20landscape%20elements%2C%20rustic%20circular%20brand%20identity%20for%20regional%20music&width=120&height=120&seq=sa-radio-logo-11&orientation=squarish',
    ownerName: 'Jorge Gaitán',
    ownerEmail: 'jgaitan@lavozdelllano.co',
    streamUrl: 'https://stream.lavozdelllano.co/live',
    redirectCode: 'voz-del-llano',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-07T09:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-06-07T14:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-06-08T11:00:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'María Gómez (SA)', changedAt: '2026-06-08T11:01:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 48, state: 'activa', factors: { streamActivo: 60, publicaciones: 35, eventos: 20, respuestasCabina: 42, lives: 30, interaccion: 50 } },
    communityStats: { followers: 560, posts: 28, comments: 72, events: 2, interactions: 650 },
  },
  {
    id: 12,
    name: 'Radio Universo Online',
    frequency: '95.80 RFW',
    city: 'Montevideo',
    country: 'Uruguay',
    plan: 'free',
    status: 'en_revision',
    streamStatus: 'inactivo',
    streamStatusV2: 'sin_respuesta',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.1,
    storageTotalGB: 1,
    registeredAt: '2026-06-15',
    listeners: 0,
    category: 'Indie / Alternativo',
    slogan: 'Otra forma de escuchar',
    logoUrl: 'https://readdy.ai/api/search-image?query=Indie%20radio%20station%20logo%2C%20minimalist%20cosmic%20design%2C%20deep%20purple%20and%20teal%20space%20colors%2C%20planet%20and%20sound%20wave%20elements%2C%20modern%20alternative%20brand%20identity%20for%20young%20creatives&width=120&height=120&seq=sa-radio-logo-12&orientation=squarish',
    ownerName: 'Camila Sosa',
    ownerEmail: 'csosa@universonline.uy',
    streamUrl: 'https://stream.universonline.uy/live',
    redirectCode: 'universo-online',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-15T08:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-06-15T14:30:00', reason: 'Inicio de revisión de documentación y verificación de stream' },
    ],
    radioScore: { score: 10, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 5, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 2 } },
    communityStats: { followers: 85, posts: 3, comments: 5, events: 0, interactions: 12 },
  },
  {
    id: 13,
    name: 'Radio Festival TV',
    frequency: '100.20 RFW',
    city: 'Quito',
    country: 'Ecuador',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 22.8,
    storageTotalGB: 50,
    registeredAt: '2025-08-05',
    listeners: 3210,
    category: 'Festival / En Vivo',
    slogan: 'Donde la música vive',
    logoUrl: 'https://readdy.ai/api/search-image?query=Festival%20radio%20station%20logo%2C%20vibrant%20confetti%20colorful%20design%2C%20orange%20magenta%20and%20yellow%2C%20stage%20lights%20elements%2C%20energetic%20circular%20brand%20identity%20for%20live%20events&width=120&height=120&seq=sa-radio-logo-13&orientation=squarish',
    ownerName: 'Daniela Paz',
    ownerEmail: 'dpaz@festivaltv.ec',
    streamUrl: 'https://stream.festivaltv.ec/live',
    redirectCode: 'festival-tv',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-08-05T10:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2025-08-06T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2025-08-08T13:00:00', reason: 'Documentación verificada. Frecuencia 100.20 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-08-08T13:01:00', reason: 'Contrato Pro generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-08-09T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-08-11T15:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-08-12T09:45:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 76, state: 'muy_activa', factors: { streamActivo: 82, publicaciones: 70, eventos: 75, respuestasCabina: 78, lives: 68, interaccion: 80 } },
    communityStats: { followers: 5200, posts: 185, comments: 580, events: 18, interactions: 7200 },
  },
  {
    id: 14,
    name: 'Radio Brisas del Mar',
    frequency: '89.70 RFW',
    city: 'Acapulco',
    country: 'México',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'caido',
    streamStatusV2: 'offline',
    daysOffline: 25,
    warningLevel: 'en_riesgo',
    warningMessage: '25 días sin stream — Radio en riesgo. Plan Pro: Super Admin debe decidir.',
    recommendedAction: 'en_riesgo',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    storageUsedGB: 15.6,
    storageTotalGB: 50,
    registeredAt: '2025-11-20',
    listeners: 1890,
    category: 'Tropical / Cumbia',
    slogan: 'La brisa que alegra tu día',
    logoUrl: 'https://readdy.ai/api/search-image?query=Tropical%20radio%20station%20logo%2C%20ocean%20breeze%20theme%2C%20turquoise%20and%20sandy%20beige%20colors%2C%20palm%20tree%20and%20sun%20elements%2C%20relaxed%20circular%20brand%20identity%20for%20beach%20vibes&width=120&height=120&seq=sa-radio-logo-14&orientation=squarish',
    ownerName: 'Roberto Núñez',
    ownerEmail: 'rnunez@brisasdelmar.mx',
    streamUrl: 'https://stream.brisasdelmar.mx/live',
    redirectCode: 'brisas-del-mar',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-11-20T11:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2025-11-21T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2025-11-23T10:30:00', reason: 'Documentación verificada. Frecuencia 89.70 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-11-23T10:31:00', reason: 'Contrato Pro generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-11-24T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-11-26T12:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-11-27T14:30:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 18, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 25, eventos: 10, respuestasCabina: 15, lives: 5, interaccion: 20 } },
    communityStats: { followers: 210, posts: 12, comments: 28, events: 0, interactions: 190 },
  },
  {
    id: 15,
    name: 'Radio Juventud Stereo',
    frequency: '104.50 RFW',
    city: 'Ciudad de Panamá',
    country: 'Panamá',
    plan: 'free',
    status: 'cancelada',
    streamStatus: 'inactivo',
    streamStatusV2: 'sin_respuesta',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.1,
    storageTotalGB: 1,
    registeredAt: '2026-03-12',
    listeners: 0,
    category: 'Pop / Juvenil',
    slogan: 'La radio de los jóvenes',
    logoUrl: 'https://readdy.ai/api/search-image?query=Youth%20radio%20station%20logo%2C%20bright%20cheerful%20design%2C%20neon%20cyan%20and%20hot%20pink%20colors%2C%20emoji%20and%20digital%20elements%2C%20modern%20circular%20brand%20identity%20for%20teen%20audience&width=120&height=120&seq=sa-radio-logo-15&orientation=squarish',
    ownerName: 'Melissa Chang',
    ownerEmail: 'mchang@juventudstereo.pa',
    streamUrl: 'https://stream.juventudstereo.pa/live',
    redirectCode: 'juventud-stereo',
    customDomain: null,
    rejectedReason: 'Información de propiedad incompleta. No se pudo verificar identidad.',
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-03-12T14:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-03-13T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'cancelada', changedBy: 'María Gómez (SA)', changedAt: '2026-03-18T11:30:00', reason: 'Información de propiedad incompleta. No se pudo verificar identidad del solicitante.' },
    ],
    radioScore: { score: 3, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 0, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 0 } },
    communityStats: { followers: 15, posts: 0, comments: 0, events: 0, interactions: 0 },
  },
  {
    id: 16,
    name: 'Radio Folclor Andino',
    frequency: '91.40 RFW',
    city: 'Cusco',
    country: 'Perú',
    plan: 'free',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.8,
    storageTotalGB: 1,
    registeredAt: '2026-05-01',
    listeners: 445,
    category: 'Folclor / Andina',
    slogan: 'El alma de los Andes',
    logoUrl: 'https://readdy.ai/api/search-image?query=Andean%20folk%20radio%20logo%2C%20traditional%20mountain%20design%2C%20warm%20earth%20tones%20terracotta%20and%20ochre%2C%20quena%20flute%20and%20mountain%20elements%2C%20circular%20cultural%20brand%20identity&width=120&height=120&seq=sa-radio-logo-16&orientation=squarish',
    ownerName: 'Humberto Quispe',
    ownerEmail: 'hquispe@folclorandino.pe',
    streamUrl: 'https://stream.folclorandino.pe/live',
    redirectCode: 'folclor-andino',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-05-01T07:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-05-02T10:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-05-04T09:00:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'María Gómez (SA)', changedAt: '2026-05-04T09:01:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 55, state: 'activa', factors: { streamActivo: 68, publicaciones: 42, eventos: 38, respuestasCabina: 50, lives: 35, interaccion: 58 } },
    communityStats: { followers: 780, posts: 38, comments: 105, events: 3, interactions: 940 },
  },
  {
    id: 17,
    name: 'Radio Retro Hits',
    frequency: '98.60 RFW',
    city: 'Asunción',
    country: 'Paraguay',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 28.3,
    storageTotalGB: 50,
    registeredAt: '2025-02-28',
    listeners: 2678,
    category: 'Retro / Clásicos',
    slogan: 'Los éxitos de siempre',
    logoUrl: 'https://readdy.ai/api/search-image?query=Retro%20radio%20station%20logo%2C%20vintage%20vinyl%20record%20design%2C%20warm%20sepia%20and%20mustard%20colors%2C%20nostalgic%2080s%20aesthetic%2C%20circular%20classic%20brand%20identity%20for%20oldies%20station&width=120&height=120&seq=sa-radio-logo-17&orientation=squarish',
    ownerName: 'Fernando López',
    ownerEmail: 'flopez@retrohits.py',
    streamUrl: 'https://stream.retrohits.py/live',
    redirectCode: 'retro-hits',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-02-28T13:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2025-03-01T08:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2025-03-03T15:00:00', reason: 'Documentación verificada. Frecuencia 98.60 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-03-03T15:01:00', reason: 'Contrato Pro generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-03-04T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-03-06T11:00:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-03-07T16:00:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 74, state: 'muy_activa', factors: { streamActivo: 80, publicaciones: 68, eventos: 72, respuestasCabina: 75, lives: 62, interaccion: 78 } },
    communityStats: { followers: 4800, posts: 172, comments: 540, events: 14, interactions: 6100 },
  },
  {
    id: 18,
    name: 'Radio TechBeats',
    frequency: '106.20 RFW',
    city: 'San Salvador',
    country: 'El Salvador',
    plan: 'pro',
    status: 'pendiente_contrato',
    streamStatus: 'inactivo',
    streamStatusV2: 'sin_respuesta',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.05,
    storageTotalGB: 50,
    registeredAt: '2026-06-14',
    listeners: 0,
    category: 'Electrónica / Techno',
    slogan: 'El pulso de la noche',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20logo%2C%20futuristic%20cyberpunk%20design%2C%20dark%20background%20with%20neon%20green%20and%20electric%20blue%2C%20digital%20waveform%2C%20sleek%20modern%20brand%20identity%20for%20tech%20music&width=120&height=120&seq=sa-radio-logo-18&orientation=squarish',
    ownerName: 'Óscar Duarte',
    ownerEmail: 'oduarte@techbeats.sv',
    streamUrl: 'https://stream.techbeats.sv/live',
    redirectCode: 'tech-beats',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-14T10:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-06-14T15:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'María Gómez (SA)', changedAt: '2026-06-15T09:30:00', reason: 'Documentación verificada. Frecuencia 106.20 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2026-06-15T09:31:00', reason: 'Contrato Pro generado. Esperando revisión del dueño.' },
    ],
    radioScore: { score: 12, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 8, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 5 } },
    communityStats: { followers: 120, posts: 5, comments: 8, events: 0, interactions: 18 },
  },
  {
    id: 19,
    name: 'Radio Deportiva Total',
    frequency: '93.80 RFW',
    city: 'Guadalajara',
    country: 'México',
    plan: 'pro',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 34.5,
    storageTotalGB: 50,
    registeredAt: '2025-04-10',
    listeners: 5432,
    category: 'Deportes',
    slogan: 'Pasión por el deporte',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%2C%20dynamic%20energetic%20design%2C%20bold%20red%20and%20white%20colors%2C%20soccer%20ball%20and%20microphone%20elements%2C%20circular%20action%20brand%20identity%20for%20sports%20fans&width=120&height=120&seq=sa-radio-logo-19&orientation=squarish',
    ownerName: 'Héctor Gómez',
    ownerEmail: 'hgomez@deportivatotal.mx',
    streamUrl: 'https://stream.deportivatotal.mx/live',
    redirectCode: 'deportiva-total',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2025-04-10T09:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2025-04-11T08:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2025-04-13T12:00:00', reason: 'Documentación verificada. Frecuencia 93.80 RFW asignada.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'pendiente_contrato', changedBy: 'Sistema', changedAt: '2025-04-13T12:01:00', reason: 'Contrato Pro generado automáticamente' },
      { id: 5, fromStatus: 'pendiente_contrato', toStatus: 'pendiente_firma', changedBy: 'Sistema', changedAt: '2025-04-14T09:00:00', reason: 'Contrato enviado para firma electrónica' },
      { id: 6, fromStatus: 'pendiente_firma', toStatus: 'pendiente_pago', changedBy: 'Sistema', changedAt: '2025-04-16T10:30:00', reason: 'Firma electrónica completada. Pendiente de pago inicial.' },
      { id: 7, fromStatus: 'pendiente_pago', toStatus: 'activa', changedBy: 'Sistema', changedAt: '2025-04-17T14:00:00', reason: 'Pago inicial confirmado. Radio activada.' },
    ],
    radioScore: { score: 82, state: 'excelente', factors: { streamActivo: 88, publicaciones: 78, eventos: 80, respuestasCabina: 84, lives: 72, interaccion: 86 } },
    communityStats: { followers: 8200, posts: 298, comments: 1120, events: 20, interactions: 13200 },
  },
  {
    id: 20,
    name: 'Radio Gospel Internacional',
    frequency: '87.90 RFW',
    city: 'Miami',
    country: 'Estados Unidos',
    plan: 'free',
    status: 'activa',
    streamStatus: 'activo',
    streamStatusV2: 'online',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.9,
    storageTotalGB: 1,
    registeredAt: '2026-01-18',
    listeners: 987,
    category: 'Gospel / Cristiana',
    slogan: 'Música que eleva el alma',
    logoUrl: 'https://readdy.ai/api/search-image?query=Gospel%20radio%20station%20logo%2C%20spiritual%20serene%20design%2C%20soft%20gold%20and%20white%20colors%2C%20dove%20and%20light%20rays%20elements%2C%20circular%20peaceful%20brand%20identity%20for%20Christian%20music&width=120&height=120&seq=sa-radio-logo-20&orientation=squarish',
    ownerName: 'Pastor David Jiménez',
    ownerEmail: 'djimenez@gospelinternacional.com',
    streamUrl: 'https://stream.gospelinternacional.com/live',
    redirectCode: 'gospel-internacional',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-01-18T11:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'Admin Global', changedAt: '2026-01-19T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'aprobada', changedBy: 'Admin Global', changedAt: '2026-01-21T10:00:00', reason: 'Documentación verificada. Plan Free aprobado.' },
      { id: 4, fromStatus: 'aprobada', toStatus: 'activa', changedBy: 'Admin Global', changedAt: '2026-01-21T10:01:00', reason: 'Activación directa por plan Free.' },
    ],
    radioScore: { score: 62, state: 'muy_activa', factors: { streamActivo: 75, publicaciones: 55, eventos: 48, respuestasCabina: 60, lives: 45, interaccion: 65 } },
    communityStats: { followers: 1850, posts: 82, comments: 245, events: 6, interactions: 2100 },
  },
  {
    id: 21,
    name: 'Radio Deep House Nights',
    frequency: '92.80 RFW',
    city: 'Valparaíso',
    country: 'Chile',
    plan: 'free',
    status: 'solicitud_recibida',
    streamStatus: 'inactivo',
    streamStatusV2: 'sin_respuesta',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.02,
    storageTotalGB: 1,
    registeredAt: '2026-06-16',
    listeners: 0,
    category: 'House / Deep',
    slogan: 'El lado profundo de la noche',
    logoUrl: 'https://readdy.ai/api/search-image?query=Deep%20house%20radio%20logo%2C%20moody%20atmospheric%20design%2C%20dark%20teal%20and%20electric%20indigo%20colors%2C%20geometric%20wave%20patterns%2C%20sophisticated%20modern%20brand%20identity%20for%20electronic%20music&width=120&height=120&seq=sa-radio-logo-21&orientation=squarish',
    ownerName: 'Tomás Rivera',
    ownerEmail: 'trivera@deephousenights.cl',
    streamUrl: 'https://stream.deephousenights.cl/live',
    redirectCode: 'deep-house-nights',
    customDomain: null,
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-16T12:30:00', reason: 'Solicitud de ingreso enviada por el dueño' },
    ],
    radioScore: { score: 6, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 2, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 0 } },
    communityStats: { followers: 40, posts: 1, comments: 2, events: 0, interactions: 5 },
  },
  {
    id: 22,
    name: 'Radio Azul Tropical',
    frequency: '97.30 RFW',
    city: 'San Juan',
    country: 'Puerto Rico',
    plan: 'free',
    status: 'archivada',
    streamStatus: 'inactivo',
    streamStatusV2: 'sin_respuesta',
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    storageUsedGB: 0.03,
    storageTotalGB: 1,
    registeredAt: '2026-06-10',
    listeners: 0,
    category: 'Tropical / Merengue',
    slogan: 'El sabor del Caribe',
    logoUrl: 'https://readdy.ai/api/search-image?query=Tropical%20Caribbean%20radio%20logo%2C%20bright%20turquoise%20and%20sunny%20yellow%20design%2C%20palm%20silhouette%20and%20music%20note%20elements%2C%20circular%20vibrant%20brand%20identity%20for%20island%20music&width=120&height=120&seq=sa-radio-logo-22&orientation=squarish',
    ownerName: 'Rosa Colón',
    ownerEmail: 'rcolon@azultropical.pr',
    streamUrl: 'https://stream.azultropical.pr/live',
    redirectCode: 'azul-tropical',
    customDomain: null,
    rejectedReason: 'La frecuencia solicitada ya está asignada. Se requiere cambio.',
    statusHistory: [
      { id: 1, fromStatus: null, toStatus: 'solicitud_recibida', changedBy: 'Sistema', changedAt: '2026-06-10T15:00:00', reason: 'Solicitud de ingreso enviada por el dueño' },
      { id: 2, fromStatus: 'solicitud_recibida', toStatus: 'en_revision', changedBy: 'María Gómez (SA)', changedAt: '2026-06-11T09:00:00', reason: 'Inicio de revisión de documentación' },
      { id: 3, fromStatus: 'en_revision', toStatus: 'cancelada', changedBy: 'María Gómez (SA)', changedAt: '2026-06-13T11:00:00', reason: 'La frecuencia solicitada 97.30 RFW ya está asignada a otra radio. Se notificó al solicitante.' },
      { id: 4, fromStatus: 'cancelada', toStatus: 'archivada', changedBy: 'Sistema', changedAt: '2026-06-20T00:00:00', reason: 'Archivado automático tras 7 días en estado cancelada.' },
    ],
    radioScore: { score: 4, state: 'inactiva', factors: { streamActivo: 0, publicaciones: 0, eventos: 0, respuestasCabina: 0, lives: 0, interaccion: 0 } },
    communityStats: { followers: 10, posts: 0, comments: 0, events: 0, interactions: 0 },
  },
];